/**
 * Подбор ширины колонок таблицы.
 *
 * Два режима:
 *
 *  - `content` — ширина под самую длинную ячейку, чтобы текст помещался в одну
 *    строку. Хорошо для узких таблиц, но при длинных текстовых полях таблица
 *    вылезает за экран.
 *
 *  - `fit` — доступная ширина делится между колонками так, чтобы таблица
 *    поместилась в экран, а перенос текста был примерно одинаковым во всех
 *    колонках. Ширина раздаётся пропорционально объёму текста: высота строки —
 *    это максимум по колонкам, и он минимален, когда все колонки ломаются на
 *    одинаковое число строк.
 *
 * Файл намеренно не знает про DOM — только арифметика, чтобы её можно было
 * покрыть тестами. Замеры текста делает вызывающая сторона.
 */

export const MIN_COL_WIDTH = 80;
export const MAX_COL_WIDTH = 560;

/** Потолок ширины по типу поля: дату или число незачем растягивать. */
const TYPE_CAPS: Record<string, number> = {
  boolean: 70,
  number: 120,
  computed: 120,
  date: 120,
  select: 200,
  user: 200,
  reference: 240,
  multi_select: 280,
  url: 260,
  text: 420,
  textarea: 520,
  markdown: MAX_COL_WIDTH,
};

/**
 * Типы, которые имеет смысл переносить на несколько строк. Дату, число и статус
 * переносить бессмысленно — им ширина выдаётся фиксированная, и они не
 * участвуют в дележе.
 */
const WRAPPABLE_TYPES = new Set(['text', 'textarea', 'markdown', 'multi_select', 'reference', 'url']);

export function capForType(fieldType?: string): number {
  return (fieldType ? TYPE_CAPS[fieldType] : undefined) ?? 300;
}

export function isWrappable(fieldType?: string): boolean {
  return fieldType ? WRAPPABLE_TYPES.has(fieldType) : false;
}

export interface ColumnMeasure {
  slug: string;
  /** Ширина заголовка вместе с отступами и иконкой перетаскивания. */
  headerWidth: number;
  /** Самое длинное слово заголовка: заголовок тоже переносится на две строки. */
  headerWordWidth: number;
  /** Самое длинное неразрывное слово — уже этой ширины колонку не сжать. */
  longestWord: number;
  /** Типичная ширина текста в одну строку (90-й перцентиль по строкам). */
  contentWidth: number;
  /** Ширина самой длинной ячейки в одну строку. */
  maxContentWidth: number;
  wrappable: boolean;
  cap: number;
}

/** Значение p-го перцентиля (p от 0 до 1). Пустой массив даёт 0. */
export function percentile(values: number[], p: number): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const idx = Math.min(sorted.length - 1, Math.max(0, Math.ceil(p * sorted.length) - 1));
  return sorted[idx] ?? 0;
}

/**
 * Минимальная ширина колонки — ниже неё текст уже не переносится, а рвётся.
 * Заголовок в расчёт входит самым длинным словом, а не целиком: в режиме
 * переноса он тоже разъезжается на две строки, и требовать под него всю ширину
 * значит зря раздувать узкие колонки вроде «Оценка Analyst, дней».
 */
function hardMin(c: ColumnMeasure): number {
  const contentFloor = c.wrappable
    ? Math.min(c.longestWord, c.cap)
    : Math.min(c.maxContentWidth, c.cap);
  const headerFloor = Math.min(c.headerWordWidth || c.headerWidth, c.cap);
  return Math.round(Math.max(MIN_COL_WIDTH, headerFloor, contentFloor));
}

/**
 * Раздаёт `available` пикселей между колонками.
 *
 * Если всем хватает желаемой ширины — раздаём её, а остаток отдаём переносимым
 * колонкам (но не больше, чем нужно их самому длинному тексту).
 *
 * Если не хватает — делим ширину пропорционально объёму текста, зажимая каждую
 * колонку в её границы; зажатые фиксируются, остаток делится заново.
 */
export function distributeWidths(cols: ColumnMeasure[], available: number): Record<string, number> {
  const result: Record<string, number> = {};
  if (cols.length === 0) return result;

  interface Sized { col: ColumnMeasure; min: number; want: number }
  const sized: Sized[] = cols.map((col) => {
    const min = hardMin(col);
    return { col, min, want: Math.max(min, Math.round(Math.min(col.contentWidth, col.cap))) };
  });
  const totalWant = sized.reduce((acc, s) => acc + s.want, 0);
  const widthOf = (slug: string): number => result[slug] ?? 0;

  if (totalWant <= available) {
    for (const s of sized) result[s.col.slug] = s.want;
    let extra = available - totalWant;
    // Остаток раздаём переносимым колонкам, пропорционально тому, сколько им
    // не хватает до полного текста в одну строку.
    let growable = sized.filter((s) => s.col.wrappable && s.col.maxContentWidth > widthOf(s.col.slug));
    for (let guard = 0; guard < 4 && extra > 1 && growable.length > 0; guard += 1) {
      const headroom = growable.reduce((acc, s) => acc + (s.col.maxContentWidth - widthOf(s.col.slug)), 0);
      if (headroom <= 0) break;
      const share = Math.min(1, extra / headroom);
      for (const s of growable) {
        const w = widthOf(s.col.slug);
        result[s.col.slug] = Math.round(w + (s.col.maxContentWidth - w) * share);
      }
      extra = available - sized.reduce((acc, s) => acc + widthOf(s.col.slug), 0);
      if (share >= 1) break;
      growable = growable.filter((s) => s.col.maxContentWidth > widthOf(s.col.slug));
    }
    return result;
  }

  // Даже минимумы не влезают (много колонок на узком экране). Сжимать дальше
  // некуда: колонки станут нечитаемыми, а прокрутка всё равно останется.
  // Отдаём желаемые ширины и оставляем горизонтальную прокрутку.
  const totalMin = sized.reduce((acc, s) => acc + s.min, 0);
  if (totalMin >= available) {
    for (const s of sized) result[s.col.slug] = s.want;
    return result;
  }

  // Ширины не хватает: делим доступную пропорционально объёму текста.
  let flexible: Sized[] = [];
  let remaining = available;
  for (const s of sized) {
    if (s.want <= s.min) {
      // Колонку не сжать и не растянуть (непереносимая или уже в минимуме).
      result[s.col.slug] = s.min;
      remaining -= s.min;
    } else {
      flexible.push(s);
    }
  }

  for (let guard = 0; guard <= cols.length && flexible.length > 0; guard += 1) {
    const totalArea = flexible.reduce((acc, s) => acc + Math.max(1, s.col.contentWidth), 0);
    let clampedSlug: string | null = null;
    for (const s of flexible) {
      const raw = (remaining * Math.max(1, s.col.contentWidth)) / totalArea;
      if (raw < s.min || raw > s.want) {
        const fixed = raw < s.min ? s.min : s.want;
        result[s.col.slug] = fixed;
        remaining -= fixed;
        clampedSlug = s.col.slug;
        // Остальным доли пересчитываем заново — остаток изменился.
        break;
      }
    }
    if (clampedSlug === null) {
      for (const s of flexible) {
        result[s.col.slug] = Math.round((remaining * Math.max(1, s.col.contentWidth)) / totalArea);
      }
      flexible = [];
    } else {
      flexible = flexible.filter((s) => s.col.slug !== clampedSlug);
    }
  }

  // На случай выхода цикла по guard'у — недоделённым колонкам отдаём минимум.
  for (const s of sized) {
    if (result[s.col.slug] === undefined) result[s.col.slug] = s.min;
  }
  return result;
}
